/* eslint-disable no-console */
import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";

import { getToken } from "../Utils/helper";
import { buildTicketedWebSocketURL } from "../api/api";
import { clearWebSocketStatus, handleWebSocketClose } from "../auth/websocketLifecycle";
import {
  setConnectionStatus,
  updateLastUpdate,
} from "../Redux/checklist-websocket/checklistWebSocketSlice";

const ChecklistWebSocketContext = createContext(null);
const SOCKET_PATH = "/ws/automation/";

export const useChecklistWebSocketContext = () => {
  const context = useContext(ChecklistWebSocketContext);
  if (!context) {
    throw new Error("useChecklistWebSocketContext must be used within ChecklistWebSocketProvider");
  }
  return context;
};

/**
 * Global WebSocket provider for checklist automation
 * Maintains WebSocket connection for real-time checklist updates
 * Connects with a short-lived, single-use WebSocket ticket.
 */
export const ChecklistWebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const connectionAttemptRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const currentCaseRef = useRef(null);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 100000;

  // Calculate reconnection delay with exponential backoff
  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    );
    return delay;
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("📨 Checklist WebSocket message received:", message);

        // Handle error messages
        if (message.type === "error") {
          console.error("❌ Checklist WebSocket error message:", message.message);
          if (message.message && message.message.includes("Token expired")) {
            wsRef.current?.close(4401, "Authentication failed");
          }
          return;
        }

        // Handle connection confirmation
        if (message.type === "connected") {
          console.log("✅ Checklist WebSocket connected:", message.message);
          return;
        }

        // Validate message structure
        if (!message.type || !message.data) {
          console.error("❌ Invalid WebSocket message format:", message);
          return;
        }

        // Process known message types
        const knownTypes = [
          "checklist_updated",
          "panel_checklist_updated",
          "status_updated",
          "stage_updated",
          "cumulative_percentage_updated",
          "panel_page_blocked",
          "panel_page_unblocked",
          "page_verified",
          "page_unverified",
        ];
        if (!knownTypes.includes(message.type)) {
          console.warn("⚠️ Unknown message type:", message.type);
          return;
        }

        // Extract case_id from current connection
        const caseId = currentCaseRef.current;
        if (!caseId) {
          console.warn("⚠️ No current case ref, skipping message");
          return;
        }

        // Update last update timestamp
        dispatch(
          updateLastUpdate({
            caseId,
            timestamp: Date.now(),
          })
        );

        // Broadcast message to any component listeners via CustomEvent
        // Use specific event names for different update types
        if (message.type === "status_updated") {
          window.dispatchEvent(
            new CustomEvent("statusUpdate", {
              detail: message,
            })
          );
        } else if (message.type === "stage_updated") {
          window.dispatchEvent(
            new CustomEvent("stageUpdate", {
              detail: message,
            })
          );
        } else if (message.type === "cumulative_percentage_updated") {
          window.dispatchEvent(
            new CustomEvent("cumulativePercentageUpdate", {
              detail: message,
            })
          );
        } else if (message.type === "panel_page_blocked") {
          window.dispatchEvent(
            new CustomEvent("panel_page_blocked", {
              detail: message,
            })
          );
        } else if (message.type === "panel_page_unblocked") {
          window.dispatchEvent(
            new CustomEvent("panel_page_unblocked", {
              detail: message,
            })
          );
        } else if (message.type === "page_verified") {
          window.dispatchEvent(
            new CustomEvent("page_verified", {
              detail: message,
            })
          );
        } else if (message.type === "page_unverified") {
          window.dispatchEvent(
            new CustomEvent("page_unverified", {
              detail: message,
            })
          );
        } else {
          // Checklist updates (page and panel)
          window.dispatchEvent(
            new CustomEvent("checklistUpdate", {
              detail: message,
            })
          );
        }
      } catch (error) {
        console.error("❌ Error parsing Checklist WebSocket message:", error);
      }
    },
    [dispatch]
  );

  // Connect to WebSocket for a specific case
  const connect = useCallback(
    async (caseId) => {
      const token = getToken();

      if (!caseId || !token) {
        console.warn("⚠️ Missing required parameters for Checklist WebSocket connection");
        return;
      }

      const caseKey = String(caseId);

      // Multiple checklist/status/stage hooks can request the same connection
      // while ticket issuance is still pending. Treat that pending request and
      // a CONNECTING socket as an existing case connection.
      if (
        connectionAttemptRef.current?.caseKey === caseKey ||
        (wsRef.current &&
          [WebSocket.CONNECTING, WebSocket.OPEN].includes(wsRef.current.readyState) &&
          currentCaseRef.current === caseKey)
      ) {
        console.log("✅ Already connecting or connected to case", caseKey);
        return;
      }

      const connectionAttempt = { caseKey };
      const previousCaseKey = currentCaseRef.current;
      connectionAttemptRef.current = connectionAttempt;
      currentCaseRef.current = caseKey;

      // Clean up existing connection
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log("🔌 Closing existing Checklist WebSocket connection");
        const previousSocket = wsRef.current;
        wsRef.current = null;
        previousSocket.close(1000, "Switching to new case");
        if (previousCaseKey) {
          dispatch(setConnectionStatus({ caseId: previousCaseKey, isConnected: false }));
        }
      }

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      try {
        const wsUrl = await buildTicketedWebSocketURL(SOCKET_PATH, {
          case_id: caseKey,
        });

        if (connectionAttemptRef.current !== connectionAttempt) {
          return;
        }

        console.log("📡 Connecting Checklist WebSocket");

        const websocket = new WebSocket(wsUrl);

        websocket.onopen = () => {
          if (wsRef.current !== websocket || currentCaseRef.current !== caseKey) {
            websocket.close(1000, "Superseded connection");
            return;
          }
          console.log("✅ Checklist WebSocket connected successfully to case", caseKey);
          dispatch(setConnectionStatus({ caseId: caseKey, isConnected: true }));
          reconnectAttemptsRef.current = 0;
          clearWebSocketStatus(SOCKET_PATH);
        };

        websocket.onmessage = handleMessage;

        websocket.onerror = (error) => {
          console.error("❌ Checklist WebSocket error:", error);
        };

        websocket.onclose = (event) => {
          if (wsRef.current !== websocket) {
            return;
          }
          wsRef.current = null;
          console.log("🔌 Checklist WebSocket closed:", event.code, event.reason);
          dispatch(setConnectionStatus({ caseId: caseKey, isConnected: false }));

          if (!handleWebSocketClose(event, SOCKET_PATH)) {
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
            return;
          }

          // Don't reconnect if closed intentionally.
          if (event.code === 1000) {
            console.log("🔌 WebSocket closed intentionally, not reconnecting");
            return;
          }

          // Only attempt to reconnect if this is still the current case
          if (
            currentCaseRef.current === caseKey &&
            reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
          ) {
            const delay = getReconnectDelay();
            console.log(
              `🔄 Reconnecting in ${delay}ms... (Attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null;
              reconnectAttemptsRef.current += 1;
              connect(caseKey);
            }, delay);
          } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
            console.error("❌ Max reconnection attempts reached for case", caseKey);
          }
        };

        wsRef.current = websocket;
        connectionAttemptRef.current = null;
      } catch (error) {
        if (connectionAttemptRef.current === connectionAttempt) {
          connectionAttemptRef.current = null;
          if (!wsRef.current) {
            currentCaseRef.current = null;
          }
        }
        console.error("❌ Error creating Checklist WebSocket connection:", error);
      }
    },
    [dispatch, handleMessage, getReconnectDelay]
  );

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    const disconnectedCaseKey = currentCaseRef.current;
    connectionAttemptRef.current = null;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      console.log("🔌 Disconnecting Checklist WebSocket");
      const websocket = wsRef.current;
      wsRef.current = null;
      websocket.close(1000, "Disconnecting");
    }

    currentCaseRef.current = null;
    if (disconnectedCaseKey) {
      dispatch(setConnectionStatus({ caseId: disconnectedCaseKey, isConnected: false }));
    }
  }, [dispatch]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        } catch (error) {
          console.error("❌ Error sending ping:", error);
        }
      }
    }, 100000); // Ping every 1 min
    return () => clearInterval(interval);
  }, []); // Run once on mount

  // Auto-connect on mount when case_id is available
  useEffect(() => {
    const initialCaseId = localStorage.getItem("case_id");
    if (initialCaseId) {
      console.log("📡 Auto-connecting ChecklistWebSocket for case:", initialCaseId);
      connect(parseInt(initialCaseId));
    }

    // Listen for case_id changes (works for cross-tab changes)
    const handleStorageChange = (e) => {
      if (e.key === "case_id" && e.newValue) {
        console.log(
          "📡 Case changed (storage event), reconnecting ChecklistWebSocket to:",
          e.newValue
        );
        connect(parseInt(e.newValue));
      }
    };

    // Listen for custom case change event (works for same-tab changes)
    const handleCaseChange = (e) => {
      const newCaseId = e.detail?.caseId || localStorage.getItem("case_id");
      if (newCaseId) {
        console.log(
          "📡 Case changed (custom event), reconnecting ChecklistWebSocket to:",
          newCaseId
        );
        connect(parseInt(newCaseId));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("caseChanged", handleCaseChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("caseChanged", handleCaseChange);
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const value = {
    connect,
    disconnect,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  };

  return (
    <ChecklistWebSocketContext.Provider value={value}>
      {children}
    </ChecklistWebSocketContext.Provider>
  );
};
